import React, { useCallback, useMemo, useState } from "react";
import { Entity } from "@common/types/entity";
import { TransactionDirectionType, mapCategoryName } from "@common/categories";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Category } from "@common/types/category";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { usePersonalCategory } from "@hooks/usePersonalCategory";
import { useCategories, CategoryStructure } from "@hooks/useCategories";
import { useAccounts } from "@hooks/useAccounts";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { InjectedCommandOption } from "@components/commands/lib";
import commandStyles from "@components/commands/Commands.module.scss";
import { useEntityCommandOptions } from "@components/commands/useEntityCommandOptions";
import { CategoryIcon } from "@components/transactions/CategoryIcon";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { useOverlays } from "contexts/OverlayContext";
import { useBulkActions } from "./useBulkActions";
import { useBulkSetScheduleOptions } from "./useBulkSetScheduleOptions";
import { useClassCommandOptions } from "./useClassCommandOptions";

interface UseTransactionCommandOptionsParams {
    isFieldLocked: boolean;
    isSchedulesEnabled?: boolean;
    // See useClassCommandOptions — restricts counterparty / cash / accrual
    // ledger updates to this subset when set (excludes transactions matched
    // to an invoice or bill, where those fields are locked server-side).
    unlockedTransactionIds?: number[];
    // Narrower subset for the accrual ledger: also excludes transactions
    // whose accrual account a live recognition schedule owns.
    accrualLedgerUnlockedTransactionIds?: number[];
    // Excludes transactions a live recognition schedule keeps in their entity.
    entityUnlockedTransactionIds?: number[];
}

export function useTransactionCommandOptions({
    isFieldLocked,
    isSchedulesEnabled = false,
    unlockedTransactionIds,
    accrualLedgerUnlockedTransactionIds,
    entityUnlockedTransactionIds,
}: UseTransactionCommandOptionsParams) {
    const bulkActions = useBulkActions();
    const personalCategory = usePersonalCategory();
    const { openOverlay } = useOverlays();
    const canCreateCategory = useCanPerformWorkspaceAction(
        WorkspaceAction.CUSTOM_CATEGORIES_EDIT,
    );
    const [showCreateCounterparty, setShowCreateCounterparty] = useState(false);
    const [showCreateCustomer, setShowCreateCustomer] = useState(false);

    const handleEntitySelect = useCallback(
        (entity: Entity) => {
            const targetOptions = entityUnlockedTransactionIds
                ? { targetTransactionIds: entityUnlockedTransactionIds }
                : undefined;
            if (entity.isPersonal) {
                bulkActions.updateSelected(
                    { update: "categoryId", value: personalCategory.id },
                    targetOptions,
                );
            } else {
                bulkActions.updateSelected(
                    { update: "entity", value: entity },
                    targetOptions,
                );
            }
        },
        [bulkActions, personalCategory, entityUnlockedTransactionIds],
    );

    const entityOptions = useEntityCommandOptions({
        onEntitySelect: handleEntitySelect,
        disabled: isFieldLocked,
    });

    const { data: counterpartiesResponse } = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered: RegisteredCounterpartyIncludeScope.USED,
    });
    const counterparties = useMemo(
        () =>
            (counterpartiesResponse?.data ?? [])
                .map((row) => row.counterparty)
                .filter((cp): cp is NonNullable<typeof cp> => cp != null),
        [counterpartiesResponse?.data],
    );

    const buildContactOptions = useCallback(
        ({
            idPrefix,
            createNewOption,
            onSelectContact,
        }: {
            idPrefix: string;
            createNewOption: InjectedCommandOption;
            onSelectContact: (contact: (typeof counterparties)[number]) => void;
        }): InjectedCommandOption[] => {
            const existingOptions = counterparties
                .filter((cp) => cp.name)
                .map((cp) => ({
                    id: `${idPrefix}-${cp.id}`,
                    label: cp.name,
                    keywords: [cp.name].filter(Boolean),
                    disabled: isFieldLocked,
                    onSelect: () => onSelectContact(cp),
                }));

            return [createNewOption, ...existingOptions];
        },
        [counterparties, isFieldLocked],
    );

    const counterpartyOptions: InjectedCommandOption[] = useMemo(
        () =>
            buildContactOptions({
                idPrefix: "counterparty",
                createNewOption: {
                    id: "counterparty-create-new",
                    label: "Create new counterparty",
                    keywords: ["create", "new", "add"],
                    disabled: isFieldLocked,
                    shouldClosePalette: true,
                    onSelect: () => setShowCreateCounterparty(true),
                },
                onSelectContact: (cp) => {
                    bulkActions.updateSelected(
                        {
                            update: "counterpartyId",
                            value: cp.id,
                            counterpartyName: cp.name,
                        },
                        unlockedTransactionIds
                            ? { targetTransactionIds: unlockedTransactionIds }
                            : undefined,
                    );
                },
            }),
        [
            buildContactOptions,
            bulkActions,
            isFieldLocked,
            unlockedTransactionIds,
        ],
    );

    const customerOptions: InjectedCommandOption[] = useMemo(
        () =>
            buildContactOptions({
                idPrefix: "customer",
                createNewOption: {
                    id: "customer-create-new",
                    label: "Create new billable customer",
                    keywords: ["create", "new", "add", "customer", "billable"],
                    disabled: isFieldLocked,
                    shouldClosePalette: true,
                    onSelect: () => setShowCreateCustomer(true),
                },
                onSelectContact: (cp) => {
                    bulkActions.updateSelected({
                        update: "customerId",
                        value: cp.id,
                        customerName: cp.name,
                    });
                },
            }),
        [buildContactOptions, bulkActions, isFieldLocked],
    );

    const categories = useCategories({ structure: CategoryStructure.TREE });
    const firstSelectedEntity = bulkActions.selectedArray[0]?.entity;
    const accounts = useAccounts({
        entityId: firstSelectedEntity?.id,
        excludeLinkedToFinancialAccount: true,
    });
    const accrualAccounts = useAccounts({
        entityId: firstSelectedEntity?.id,
        excludeLinkedToFinancialAccount: true,
        ledgerBasis: AccountingLedgerBasis.accruals,
    });

    const transactionDirection = useMemo(() => {
        const selectedArray = bulkActions.selectedArray;
        if (bulkActions.hasSelectedAll || selectedArray.length === 0) {
            return TransactionDirectionType.mixed;
        }
        const first = selectedArray[0]?.transactionDirection;
        const isMixed = selectedArray.some(
            (t) => t.transactionDirection !== first,
        );
        return isMixed ? TransactionDirectionType.mixed : first;
    }, [bulkActions.selectedArray, bulkActions.hasSelectedAll]);

    const buildCategoryOption = useCallback(
        (categoryId: string): (() => void) =>
            () =>
                bulkActions.updateSelected({
                    update: "categoryId",
                    value: categoryId,
                }),
        [bulkActions],
    );

    const handleCategoryCreated = useCallback(
        (category: Category) => {
            bulkActions.updateSelected({
                update: "categoryId",
                value: category.id,
            });
        },
        [bulkActions],
    );

    const categoryOptions: InjectedCommandOption[] = useMemo(() => {
        const options: InjectedCommandOption[] = categories.flatMap(
            (category) => {
                const parentName = mapCategoryName({
                    category,
                    transactionDirection,
                });
                const parentOption: InjectedCommandOption = {
                    id: `category-${category.id}`,
                    label: parentName,
                    icon: (
                        <CategoryIcon
                            categoryId={category.id}
                            transactionDirection={transactionDirection}
                        />
                    ),
                    keywords: [category.label, parentName].filter(Boolean),
                    disabled: isFieldLocked,
                    onSelect: buildCategoryOption(category.id),
                };
                const subcategoryOptions = (category.subcategories ?? []).map(
                    (subcategory) => {
                        const subcategoryName = mapCategoryName({
                            category: subcategory,
                            transactionDirection,
                        });
                        return {
                            id: `category-${subcategory.id}`,
                            label: subcategoryName,
                            className: commandStyles.subcategoryOption,
                            keywords: [
                                subcategory.label,
                                subcategoryName,
                                category.label,
                                parentName,
                            ].filter(Boolean),
                            disabled: isFieldLocked,
                            onSelect: buildCategoryOption(subcategory.id),
                        };
                    },
                );
                return [parentOption, ...subcategoryOptions];
            },
        );

        if (canCreateCategory) {
            options.unshift({
                id: "category-create-new",
                label: "Create new category",
                keywords: ["create", "new", "add", "category"],
                disabled: isFieldLocked,
                shouldClosePalette: true,
                onSelect: () => {
                    openOverlay<"createCategory">("createCategory", {
                        onCategoryCreated: handleCategoryCreated,
                    });
                },
            });
        }

        return options;
    }, [
        categories,
        transactionDirection,
        isFieldLocked,
        buildCategoryOption,
        canCreateCategory,
        openOverlay,
        handleCategoryCreated,
    ]);

    const buildAccountOptions = useCallback(
        ({
            accountList,
            idPrefix,
            updateKey,
            targetTransactionIds,
        }: {
            accountList: ReturnType<typeof useAccounts>["accounts"];
            idPrefix: string;
            updateKey: "coa" | "accrualCoa";
            targetTransactionIds?: number[];
        }): InjectedCommandOption[] =>
            accountList.map((account, index) => {
                const includeGroupHeader =
                    index === 0 || accountList[index - 1].type !== account.type;
                return {
                    id: `${idPrefix}-${account.id}`,
                    label: account.name ?? "",
                    keywords: [
                        account.name ?? "",
                        account.type ?? "",
                        account.codeText ?? "",
                    ].filter(Boolean),
                    disabled: isFieldLocked,
                    groupHeader: includeGroupHeader ? account.type : undefined,
                    onSelect: () => {
                        bulkActions.updateSelected(
                            {
                                update: updateKey,
                                value: account.id,
                                accountName: account.name,
                            },
                            targetTransactionIds
                                ? { targetTransactionIds }
                                : undefined,
                        );
                    },
                };
            }),
        [bulkActions, isFieldLocked],
    );

    const coaOptions: InjectedCommandOption[] = useMemo(
        () =>
            buildAccountOptions({
                accountList: accounts.accounts,
                idPrefix: "coa",
                updateKey: "coa",
                targetTransactionIds: unlockedTransactionIds,
            }),
        [buildAccountOptions, accounts.accounts, unlockedTransactionIds],
    );

    const accrualCoaOptions: InjectedCommandOption[] = useMemo(
        () =>
            buildAccountOptions({
                accountList: accrualAccounts.accounts,
                idPrefix: "accrual-coa",
                updateKey: "accrualCoa",
                targetTransactionIds:
                    accrualLedgerUnlockedTransactionIds ??
                    unlockedTransactionIds,
            }),
        [
            buildAccountOptions,
            accrualAccounts.accounts,
            accrualLedgerUnlockedTransactionIds,
            unlockedTransactionIds,
        ],
    );

    const { options: scheduleOptions, confirmModal: scheduleConfirmModal } =
        useBulkSetScheduleOptions({
            isEnabled: isSchedulesEnabled,
            isFieldLocked,
        });

    const {
        classesWithChildren: classTree,
        classesMap,
        applyClasses,
    } = useClassCommandOptions({ unlockedTransactionIds });

    return {
        entityOptions,
        counterpartyOptions,
        customerOptions,
        categoryOptions,
        coaOptions,
        accrualCoaOptions,
        scheduleOptions,
        scheduleConfirmModal,
        classTree,
        classesMap,
        applyClasses,
        showCreateCounterparty,
        setShowCreateCounterparty,
        showCreateCustomer,
        setShowCreateCustomer,
    };
}
